import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { forkJoin, Subscription } from 'rxjs';
import {
  LazyLoadFilters,
  PageRequest,
  SearchContainerComponent,
} from 'src/app/core/containers/search-container/search-container.component';
import { TabService } from 'src/app/core/services/tab.service';
import {
  FacilitySignInSearch,
  FacilitySignInSortOrQuery,
} from 'src/app/model/locations/facility-sign-in';
import { FacilityApiService } from 'src/app/services/api/facility-api.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { MUSTER_LIST_TABLE_DEFINITION } from '../../definitions/muster-list-table-definition';
import { isNullOrUndefined } from 'src/app/utils/utils';
import { FacilityBuilderService } from 'src/app/services/builder/facility-builder.service';
import { MusterListTableService } from 'src/app/services/table/muster-list-table.service';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { GeneralGridComponent } from '../../../core/components/general-grid.component';
import { BlockUIModule } from '@openng/optimus-ui/blockui';
import { ButtonModule } from '@openng/optimus-ui/button';
import { ToggleSwitchModule } from '@openng/optimus-ui/toggleswitch';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-muster-list-search-container',
  imports: [
    ProgressSpinnerComponent,
    GeneralGridComponent,
    TranslateModule,
    BlockUIModule,
    ButtonModule,
    ToggleSwitchModule,
    FormsModule,
  ],
  templateUrl: './muster-list-search-container.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './muster-list-search-container.component.scss',
})
export class MusterListSearchContainerComponent
  extends SearchContainerComponent<FacilitySignInSearch>
  implements OnInit, OnDestroy
{
  protected _musterListTableService = inject(MusterListTableService);
  protected _translateService = inject(TranslateService);
  protected _facilityApi = inject(FacilityApiService);
  protected _facilityBuilder = inject(FacilityBuilderService);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _tabService = inject(TabService);

  pageLoading = signal(true);
  facilitySignIns: WritableSignal<FacilitySignInSearch[]> = signal([]);
  showSignedOutVisitors: boolean = false;

  activeTabChangedSub!: Subscription;

  ngOnInit(): void {
    this._musterListTableService.setLastLazyLoad(null);
    this.setTableService(this._musterListTableService);
    this.columns = MUSTER_LIST_TABLE_DEFINITION;

    const screenName$ = this._translateService.get(
      'OPMONITORING.SCREEN.MUSTER_LIST_SEARCH'
    );
    forkJoin([screenName$]).subscribe((label) => {
      this._tabService.updateActiveTabLabel(label[0]);
      this.screenName = label[0];
    });

    this.applyPreviousData();
    this.applyPreviousFilters();
  }

  ngOnDestroy(): void {
    if (this.activeTabChangedSub) {
      this.activeTabChangedSub.unsubscribe();
    }
  }

  override handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ): void {
    this._musterListTableService.setLastLazyLoad($event.lazy);

    filters['signedOut'] = this.showSignedOutVisitors as unknown as any;
    const query = this._facilityBuilder.buildFacilitySignInQuery(filters);
    this.queryNetwork(req, $event, query);
  }

  queryNetwork(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    query: FacilitySignInSortOrQuery
  ): void {
    req.page += 1;
    const sort = this.buildNetworkSortQuery($event, 'signInTime');
    this.pageLoading.set(true);
    this._facilityApi
      .queryForFacilitySignIns(req.page, req.pageSize, query, sort)
      .subscribe({
        next: (response) => {
          this.facilitySignIns.set(response.items);
          this.totalRecords = response.totalRecords;
          this.elements = [...this.facilitySignIns()];
          this.pageLoading.set(false);
        },
        error: (error) => {
          this.pageLoading.set(false);
          this._logAndMessage.errorLogOnly(error);
          this._logAndMessage.translateToErrorAlert({
            bodyKey: 'OPMONITORING.MESSAGES.ERROR.QUERY_FACILITY_SIGN_INS',
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          });
        },
      });
  }

  refresh(): void {
    this.lazyLoad({ lazy: this._musterListTableService.getLastLazyLoad()! });
  }

  setupActiveTabChangedSubscription(): void {
    this.activeTabChangedSub = this._tabService.activeTabChanged.subscribe(
      (tab) => {
        if (!isNullOrUndefined(tab) && tab.header === this.screenName) {
          this.refresh();
        }
      }
    );
  }

  clearTable() {
    this.clearScreen();
    this.showSignedOutVisitors = false;
  }
}
